import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import type SchoolYear from '@/core/domain/school/year/SchoolYear.ts'
import FetchAllSchoolYearsUseCase from '@/core/useCases/school/year/FetchAllSchoolYearsUseCase.ts'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus.ts'

export const useSchoolYearStore = defineStore('SchoolYear', () => {
  // BASE COMMUNE
  const baseStore = createStoreBase(['fetchAll', 'create', 'update'])

  // ÉTAT
  const schoolYearList = ref<SchoolYear[]>([])
  const selectedSchoolYear = ref<SchoolYear | null>(null)
  const activeSchoolYear = ref<SchoolYear | null>(null)

  // ACTIONS PUBLIQUES
  /**
   * Récupère toutes les années scolaires
   * @param forceRefresh Force le rechargement même si les données sont déjà présentes
   */
  async function fetchAllSchoolYears(forceRefresh = false): Promise<SchoolYear[]> {
    // Éviter le rechargement inutile si les données sont déjà là et récentes
    if (
      !forceRefresh &&
      schoolYearList.value.length > 0 &&
      baseStore.lastUpdated.value &&
      !baseStore.shouldRefreshData()
    ) {
      return schoolYearList.value
    }

    try {
      baseStore.loading.value.fetchAll = true
      baseStore.resetError()

      const response = await DependencyContainer.resolve(FetchAllSchoolYearsUseCase).execute()
      schoolYearList.value = response
      baseStore.updateLastUpdated()

      // Sélectionner automatiquement l'année courante si aucune n'est sélectionnée
      if (!selectedSchoolYear.value) {
        const currentYear = response.find((schoolYear) => schoolYear.isCurrent)
        if (currentYear) {
          selectedSchoolYear.value = currentYear
          activeSchoolYear.value = currentYear
        }
      }

      return response
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.fetchAll = false
    }
  }

  /**
   * Sélectionne une année scolaire
   * @param schoolYear Année scolaire à sélectionner
   */
  function selectSchoolYear(schoolYear: SchoolYear | null): void {
    selectedSchoolYear.value = schoolYear

    // Émettre un événement pour que les autres stores puissent réagir
    storeEventBus.emit('schoolYear:changed', selectedSchoolYear.value)
  }

  /**
   * Définit l'année scolaire active (utilisée pour le filtrage des données)
   * @param schoolYear Année scolaire à définir comme active
   */
  function setActiveSchoolYear(schoolYear: SchoolYear | null): void {
    activeSchoolYear.value = schoolYear

    // Émettre un événement pour que les autres stores puissent réagir
    storeEventBus.emit('schoolYear:active:changed', activeSchoolYear.value)

    // Si on est sur une page qui dépend de l'année scolaire, rafraîchir les données
    refreshDataBasedOnActiveYear()
  }

  /**
   * Rafraîchit les données basées sur l'année scolaire active
   */
  function refreshDataBasedOnActiveYear(): void {
    // Émettre des événements pour rafraîchir les données des différents modules
    storeEventBus.emit('dashboard:refresh')
    storeEventBus.emit('schools:refresh')
    storeEventBus.emit('learning:refresh')
  }

  /**
   * Réinitialise le store
   */
  function reset(): void {
    schoolYearList.value = []
    selectedSchoolYear.value = null
    activeSchoolYear.value = null
    baseStore.resetBase()
  }

  // GETTERS
  /**
   * Année scolaire courante (basée sur la date actuelle)
   */
  const getCurrentSchoolYear = computed(() => {
    return schoolYearList.value.find((schoolYear) => schoolYear.isCurrent)
  })

  /**
   * Récupère une année scolaire par son ID
   */
  const getSchoolYearById = computed(() => {
    return (id: number) => schoolYearList.value.find((year) => year.schoolYearId === id)
  })

  /**
   * Années scolaires triées par date de début (plus récentes en premier)
   */
  const sortedSchoolYears = computed(() => {
    return [...schoolYearList.value].sort((a, b) => {
      const dateA = new Date(a.startDate)
      const dateB = new Date(b.startDate)
      return dateB.getTime() - dateA.getTime()
    })
  })

  /**
   * Années scolaires à venir (date de début future)
   */
  const upcomingSchoolYears = computed(() => {
    const now = new Date()
    return schoolYearList.value.filter((year) => {
      const startDate = new Date(year.startDate)
      return startDate > now
    })
  })

  /**
   * Années scolaires actives (date actuelle entre début et fin)
   */
  const activeSchoolYears = computed(() => {
    const now = new Date()
    return schoolYearList.value.filter((year) => {
      const startDate = new Date(year.startDate)
      const endDate = new Date(year.endDate)
      return startDate <= now && endDate >= now
    })
  })

  /**
   * Années scolaires passées (date de fin passée)
   */
  const pastSchoolYears = computed(() => {
    const now = new Date()
    return schoolYearList.value.filter((year) => {
      const endDate = new Date(year.endDate)
      return endDate < now
    })
  })

  /**
   * Nombre d'années scolaires
   */
  const yearCount = computed(() => schoolYearList.value.length)

  /**
   * Options pour les sélecteurs d'années scolaires
   */
  const schoolYearOptions = computed(() => {
    return schoolYearList.value.map((year) => ({
      value: year,
      label: year.name
    }))
  })

  return {
    // État
    schoolYearList,
    selectedSchoolYear,
    activeSchoolYear,
    schoolYearStoreLoading: baseStore.loading,
    schoolYearStoreError: baseStore.error,
    schoolYearStoreLastUpdated: baseStore.lastUpdated,

    // Actions
    fetchAllSchoolYears,
    selectSchoolYear,
    setActiveSchoolYear,
    reset,

    // Getters
    getCurrentSchoolYear,
    schoolYearStoreIsLoading: baseStore.isLoading,
    getSchoolYearById,
    sortedSchoolYears,
    upcomingSchoolYears,
    activeSchoolYears,
    pastSchoolYears,
    yearCount,
    schoolYearOptions
  }
})
